import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  STAFF_INVOICE_STATUSES,
  staffInvoiceFileName,
  type StaffInvoice,
} from '@/features/finance/staff-invoices';
import {
  staffInvoiceStatusLabel,
  staffInvoiceStatusVariant,
} from '@/features/finance/staff-invoices/constants';
import { formatMoney } from '@/lib/money';
import { downloadCabinetInvoice, INVOICES_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetInvoices, useSignCabinetInvoice } from '../queries';

const useInvoicesUi = createCabinetViewStore('cabinet.invoices:ui', 'table');
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type InvoiceColumn = 'number' | 'period_start' | 'base_amount' | 'status';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const fileName = (inv: StaffInvoice) => staffInvoiceFileName(inv);

export function CabinetInvoicesPage() {
  const navigate = useNavigate();
  const open = (inv: StaffInvoice) => navigate(`/me/invoices/${inv.public_id}`);
  const view = useInvoicesUi((s) => s.view);
  const setView = useInvoicesUi((s) => s.setView);
  const ordering = useInvoicesUi((s) => s.ordering);
  const setOrdering = useInvoicesUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetInvoices({
    page,
    search: search || undefined,
    ordering,
  });
  const sign = useSignCabinetInvoice();
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        search={{
          value: searchInput,
          onChange: (v) => {
            setPage(1);
            setSearchInput(v);
          },
          placeholder: 'Search number…',
        }}
        exportBase={INVOICES_BASE}
        exportParams={{ search: search || undefined, ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <InvoicesTable
          rows={rows}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onSign={(id) => sign.mutate(id)}
          signPending={sign.isPending}
          onOpen={open}
        />
      ) : view === 'list' ? (
        <InvoicesList rows={rows} onOpen={open} />
      ) : view === 'cards' ? (
        <InvoicesCards rows={rows} onOpen={open} />
      ) : (
        <InvoicesGroupedByStatus rows={rows} onOpen={open} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['invoice', 'invoices']}
      />
    </div>
  );
}

interface InvoicesTableProps {
  rows: StaffInvoice[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onSign: (publicId: string) => void;
  signPending: boolean;
  onOpen: (inv: StaffInvoice) => void;
}

function InvoicesTable({
  rows,
  ordering,
  onOrderingChange,
  onSign,
  signPending,
  onOpen,
}: InvoicesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<InvoiceColumn> column="number" value={ordering} onChange={onOrderingChange}>
              Number
            </SortableTableHead>
            <SortableTableHead<InvoiceColumn>
              column="period_start"
              value={ordering}
              onChange={onOrderingChange}
            >
              Period
            </SortableTableHead>
            <SortableTableHead<InvoiceColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Total
            </SortableTableHead>
            <SortableTableHead<InvoiceColumn> column="status" value={ordering} onChange={onOrderingChange}>
              Status
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No invoices yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => onOpen(inv)}>
                <TableCell>{inv.number || '—'}</TableCell>
                <TableCell>
                  {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(inv.total, inv.currency) ?? '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={staffInvoiceStatusVariant(inv.status)}>
                    {staffInvoiceStatusLabel(inv.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {inv.document ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadCabinetInvoice(inv.public_id, fileName(inv));
                        }}
                      >
                        <Download /> PDF
                      </Button>
                    ) : null}
                    {inv.status === 'issued' ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" onClick={(e) => e.stopPropagation()}>
                            Sign
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sign this invoice?</AlertDialogTitle>
                            <AlertDialogDescription>
                              By signing you confirm the hours and amount are correct.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onSign(inv.public_id)}
                              disabled={signPending}
                            >
                              Sign
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                    {inv.status !== 'issued' && !inv.document ? (
                      <span className="text-muted-foreground text-sm">—</span>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function InvoicesList({ rows, onOpen }: { rows: StaffInvoice[]; onOpen: (inv: StaffInvoice) => void }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((inv) => (
        <li key={inv.id}>
          <button
            type="button"
            onClick={() => onOpen(inv)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">{inv.number || 'Invoice'}</span>
            <span className="text-muted-foreground hidden sm:inline">
              {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
            </span>
            <span className="tabular-nums">{formatMoney(inv.total, inv.currency) ?? '—'}</span>
            <Badge variant={staffInvoiceStatusVariant(inv.status)} className="shrink-0">
              {staffInvoiceStatusLabel(inv.status)}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

function InvoicesCards({ rows, onOpen }: { rows: StaffInvoice[]; onOpen: (inv: StaffInvoice) => void }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((inv) => (
        <Card
          key={inv.id}
          role="button"
          tabIndex={0}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
          onClick={() => onOpen(inv)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(inv);
            }
          }}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              {inv.number || 'Invoice'}
              <Badge variant={staffInvoiceStatusVariant(inv.status)}>
                {staffInvoiceStatusLabel(inv.status)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <span>
              {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
            </span>
            <span className="ml-auto tabular-nums">{formatMoney(inv.total, inv.currency) ?? '—'}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function InvoicesGroupedByStatus({
  rows,
  onOpen,
}: {
  rows: StaffInvoice[];
  onOpen: (inv: StaffInvoice) => void;
}) {
  const groups = useMemo(
    () =>
      STAFF_INVOICE_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <InvoicesCards rows={bucket} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No invoices yet.</CabinetEmptyState>;
}
